/**
 * Chat Message Component
 * 
 * Renders a single chat message with markdown support.
 */

import { Bot, User, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: number;
  isStreaming?: boolean;
  onCopyToNotes?: (content: string) => void;
}

export function ChatMessage({ 
  role, 
  content, 
  timestamp, 
  isStreaming,
  onCopyToNotes,
}: ChatMessageProps) {
  const [copied, setCopied] = useState(false);
  const isUser = role === 'user';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cn('flex gap-3', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser
            ? 'bg-accent-blue/15'
            : 'bg-bg-tertiary'
        )}
      >
        {isUser ? (
          <User className="w-4 h-4 text-accent-blue" />
        ) : (
          <Bot className="w-4 h-4 text-text-secondary" />
        )}
      </div>

      {/* Message */}
      <div className={cn('flex-1 max-w-[85%]', isUser && 'flex flex-col items-end')}>
        <div
          className={cn(
            'rounded-lg px-4 py-2.5',
            isUser
              ? 'bg-brand-blue text-white'
              : 'bg-bg-tertiary text-text-primary'
          )}
        >
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap">{content}</p>
          ) : (
            <div className="text-sm leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:my-1.5 [&>ul]:ml-4 [&>ul]:list-disc [&>ol]:my-1.5 [&>ol]:ml-4 [&>ol]:list-decimal [&>li]:my-0.5 [&>h1]:text-base [&>h1]:font-semibold [&>h1]:mt-3 [&>h1]:mb-1 [&>h2]:text-sm [&>h2]:font-semibold [&>h2]:mt-2.5 [&>h2]:mb-1 [&>h3]:text-sm [&>h3]:font-medium [&>h3]:mt-2 [&>h3]:mb-0.5 [&>strong]:font-semibold [&>code]:bg-bg-tertiary [&>code]:px-1 [&>code]:py-0.5 [&>code]:rounded [&>code]:text-xs [&>code]:font-mono [&>pre]:bg-bg-tertiary [&>pre]:p-2 [&>pre]:rounded [&>pre]:my-2 [&>pre]:text-xs [&>pre]:overflow-x-auto">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
              {isStreaming && (
                <span className="inline-block w-1.5 h-4 bg-brand-blue animate-blink ml-0.5" />
              )}
            </div>
          )}
        </div>

        {/* Actions and timestamp */}
        <div className={cn('flex items-center gap-2 mt-1', isUser && 'flex-row-reverse')}>
          {timestamp && (
            <span className="text-xs text-text-muted">
              {new Date(timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short',
              })}
            </span>
          )}
          
          {!isUser && !isStreaming && (
            <div className="flex items-center gap-1">
              <button
                onClick={handleCopy}
                className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-secondary transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
              
              {onCopyToNotes && (
                <button
                  onClick={() => onCopyToNotes(content)}
                  className="text-xs text-text-muted hover:text-accent-blue transition-colors"
                >
                  Add to notes
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
